import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container, Typography, Grid, Card, CardContent, CardMedia,
  TextField, Select, MenuItem, ListSubheader, FormControl, InputLabel,
  Box, Chip, Button, Accordion, AccordionSummary, AccordionDetails,
  SelectChangeEvent, Checkbox, ListItemText, Skeleton, Alert,
  Stack, IconButton, Divider, Menu, Badge, Fade, CircularProgress, Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { BandRecommendation } from '../types/BandRecommendations';
import axiosInstance from '../utils/axiosConfig';
import jazzSubgenres, { popularJazzSubgenres } from '../data/jazzSubgenres';
import { Event } from '../types/mongodb';
import PublicAudioPlayer from '../components/public/PublicAudioPlayer';
import ScrollToTopButton from '../components/ScrollToTopButton';

declare global {
  interface Window { onYouTubeIframeAPIReady: () => void; }
}

interface Strand { slug: string; name: string; }

// ─── Utilities ────────────────────────────────────────────────────────────────

function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  }) as T;
}

const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const bandSizeOptions = [
  { label: 'Soloist', min: 1, max: 1 },
  { label: 'Duo', min: 2, max: 2 },
  { label: 'Trio', min: 3, max: 3 },
  { label: 'Quartet', min: 4, max: 4 },
  { label: 'Quintet', min: 5, max: 5 },
  { label: 'Sextet', min: 6, max: 6 },
  { label: 'Septet', min: 7, max: 7 },
  { label: 'Octet', min: 8, max: 8 },
  { label: 'Nonet', min: 9, max: 9 },
  { label: 'Big Band', min: 10, max: Infinity },
];

function getSizeLabel(total: number): string {
  return bandSizeOptions.find(o => total >= o.min && total <= o.max)?.label ?? `${total} members`;
}

function getDailyFeaturedBand(bands: BandRecommendation[]): BandRecommendation | null {
  if (!bands.length) return null;
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86_400_000
  );
  const seed = now.getFullYear() * 1000 + dayOfYear;
  const hash = Math.abs(((seed * 1_664_525 + 1_013_904_223) | 0));
  return bands[hash % bands.length];
}

const FILTER_PRESETS = [
  { name: 'Female-led bands', filters: { minFemaleMembers: 1, bandSize: '' } },
  { name: 'Big Bands', filters: { bandSize: 'Big Band' } },
  { name: 'Recent additions', filters: { lastViewedDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) } }
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const FeaturedHero: React.FC<{ band: BandRecommendation; onGenreClick?: (g: string) => void }> = ({ band, onGenreClick }) => {
  const description = stripHtml(band.about || '');
  const total = band.maleMembers + band.femaleMembers;
  const pressQuote = band.externalValidation?.find(v => v.featured && v.quote)
    ?? band.externalValidation?.find(v => v.quote);

  return (
    <Box
      component={Link}
      to={`/band/${band._id}`}
      sx={{
        display: 'block', textDecoration: 'none', position: 'relative',
        borderRadius: 3, overflow: 'hidden', mb: 4, cursor: 'pointer',
        '&:hover .hero-image': { transform: 'scale(1.04)' },
        '&:hover .hero-play': { opacity: 1 },
      }}
    >
      <Box className="hero-image" sx={{
        height: { xs: 340, md: 480 },
        backgroundImage: `url(${band.mainImage || '/placeholder-image.jpg'})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        transition: 'transform 0.6s ease',
      }} />
      <Box sx={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.1) 100%)',
      }} />
      <Box className="hero-play" sx={{
        position: 'absolute', top: '40%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 72, height: 72, borderRadius: '50%', bgcolor: 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: 0, transition: 'opacity 0.3s ease',
      }}>
        <PlayArrowIcon sx={{ color: 'white', fontSize: 36 }} />
      </Box>
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 4 } }}>
        <Chip label="Artist of the Day" size="small" sx={{
          mb: 1.5, bgcolor: 'primary.main', color: 'white',
          fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase',
        }} />
        <Typography variant="h3" component="h2" color="white" fontWeight={700}
          sx={{ mb: 1, textShadow: '0 2px 8px rgba(0,0,0,0.5)', lineHeight: 1.1 }}>
          {band.artistName}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: description ? 1.5 : 0 }}>
          {band.genres.map(g => (
            <Tooltip key={g} title={`Filter by ${g}`} placement="top">
              <Chip label={g} size="small"
                onClick={onGenreClick ? (e) => { e.preventDefault(); e.stopPropagation(); onGenreClick(g); } : undefined}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)', color: 'white', backdropFilter: 'blur(4px)',
                  cursor: onGenreClick ? 'pointer' : 'default',
                  '&:hover': onGenreClick ? { bgcolor: 'rgba(255,255,255,0.3)' } : {},
                }}
              />
            </Tooltip>
          ))}
          <Chip label={getSizeLabel(total)} size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.75)' }} />
        </Box>
        {description && (
          <Typography variant="body1" color="rgba(255,255,255,0.8)"
            sx={{ maxWidth: 600, display: { xs: 'none', sm: 'block' }, mb: pressQuote ? 2 : 0 }}>
            {description.substring(0, 200)}{description.length > 200 && '…'}
          </Typography>
        )}
        {pressQuote?.quote && (
          <Box sx={{
            display: { xs: 'none', md: 'flex' }, alignItems: 'flex-start', gap: 1, mt: 2,
            p: 2, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', maxWidth: 560,
          }}>
            <FormatQuoteIcon sx={{ color: 'primary.light', fontSize: 20, mt: 0.3, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" color="rgba(255,255,255,0.9)" fontStyle="italic" lineHeight={1.5}>
                {pressQuote.quote.substring(0, 140)}{pressQuote.quote.length > 140 && '…'}
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.55)" sx={{ mt: 0.5, display: 'block' }}>
                — {pressQuote.source}{pressQuote.author ? `, ${pressQuote.author}` : ''}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const GenreStrip: React.FC<{ bands: BandRecommendation[]; activeGenres: string[]; onToggle: (g: string) => void }> = ({ bands, activeGenres, onToggle }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const genresByFrequency = useMemo(() => {
    const freq: Record<string, number> = {};
    bands.forEach(b => b.genres.forEach(g => { freq[g] = (freq[g] || 0) + 1; }));
    return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([genre, count]) => ({ genre, count }));
  }, [bands]);
  if (!genresByFrequency.length) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', letterSpacing: '0.1em' }}>
        Browse by Genre
      </Typography>
      <Box ref={scrollRef} sx={{
        display: 'flex', gap: 1, overflowX: 'auto', pb: 1,
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
      }}>
        {genresByFrequency.map(({ genre, count }) => {
          const active = activeGenres.includes(genre);
          return (
            <Chip key={genre} label={`${genre} (${count})`}
              onClick={() => onToggle(genre)}
              variant={active ? 'filled' : 'outlined'}
              color={active ? 'primary' : 'default'}
              sx={{ flexShrink: 0, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 } }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const RecentlyAddedRow: React.FC<{ bands: BandRecommendation[] }> = ({ bands }) => {
  const recent = useMemo(() =>
    [...bands].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 8),
    [bands]
  );
  if (!recent.length) return null;
  return (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Recently Added</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {recent.map(band => (
          <Box key={band._id} component={Link} to={`/band/${band._id}`}
            sx={{
              textDecoration: 'none', flexShrink: 0, width: 200, borderRadius: 2,
              overflow: 'hidden', position: 'relative', transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)' },
              '&:hover .recent-overlay': { opacity: 1 },
            }}>
            <Box sx={{
              height: 140,
              backgroundImage: `url(${band.mainImage || '/placeholder-image.jpg'})`,
              backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 2,
            }} />
            <Box className="recent-overlay" sx={{
              position: 'absolute', inset: 0, borderRadius: 2,
              background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 60%)',
              opacity: 0.6, transition: 'opacity 0.3s ease',
            }} />
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.5 }}>
              <Typography variant="body2" fontWeight={700} color="white" noWrap>{band.artistName}</Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.7)" noWrap>{band.genres[0] || ''}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const EditorialPullQuote: React.FC<{
  band: BandRecommendation; quote: string; source: string; author?: string; flip?: boolean;
}> = ({ band, quote, source, author, flip = false }) => {
  const imageColor = band.mainImage ? 'var(--editorial-tint, rgba(25,118,210,0.08))' : 'transparent';
  return (
    <Box component={Link} to={`/band/${band._id}`} className="editorial-block" sx={{
      display: 'flex', flexDirection: { xs: 'column', md: flip ? 'row-reverse' : 'row' },
      textDecoration: 'none', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
      my: 1, overflow: 'hidden', position: 'relative', transition: 'border-color 0.4s ease',
      '&::before, &::after': {
        content: '""', position: 'absolute', left: '50%', right: '50%',
        height: '2px', bgcolor: 'primary.main', transition: 'left 0.45s ease, right 0.45s ease', zIndex: 1,
      },
      '&::before': { top: 0 }, '&::after': { bottom: 0 },
      '&:hover::before, &:hover::after': { left: 0, right: 0 },
      '&:hover': {
        borderColor: 'transparent',
        '& .editorial-cta': { color: 'primary.main', letterSpacing: '0.2em' },
        '& .editorial-image': { filter: 'saturate(1.15) brightness(1.05)' },
        '& .editorial-text': {
          transform: 'translateY(-4px)',
          backgroundImage: flip
            ? `linear-gradient(to left, ${imageColor}, transparent 60%)`
            : `linear-gradient(to right, ${imageColor}, transparent 60%)`,
        },
      },
    }}>
      {band.mainImage && (
        <Box className="editorial-image" sx={{
          flexShrink: 0, width: { xs: '100%', md: '30%' }, aspectRatio: { xs: '16/9', md: '1 / 1' },
          overflow: 'hidden', transition: 'filter 0.5s ease', position: 'relative',
          '& img': { width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 10s ease', transform: 'scale(1.04)' },
          '.editorial-block:hover & img': { transform: 'scale(1.22) translate(2%, -1%)', transition: 'transform 0.65s ease' },
        }}>
          <img src={band.mainImage} alt={band.artistName} />
        </Box>
      )}
      <Box className="editorial-text" sx={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
        py: { xs: 4, md: 5 }, px: { xs: 3, md: 5 },
        textAlign: { xs: 'center', md: flip ? 'right' : 'left' },
        transition: 'transform 0.4s ease, background-image 0.5s ease', backgroundImage: 'none',
      }}>
        <FormatQuoteIcon sx={{
          fontSize: 32, color: 'primary.main', opacity: 0.35, mb: 1.5,
          alignSelf: { xs: 'center', md: flip ? 'flex-end' : 'flex-start' },
          transform: flip ? 'scaleX(-1)' : 'none',
        }} />
        <Typography variant="h5" component="blockquote" fontStyle="italic" fontWeight={400}
          sx={{ lineHeight: 1.5, mb: 2, color: 'text.primary' }}>
          "{quote}"
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          — {source}{author ? `, ${author}` : ''}
        </Typography>
        <Typography className="editorial-cta" variant="overline" sx={{
          letterSpacing: '0.15em', color: 'text.disabled', transition: 'all 0.25s ease',
          display: 'inline-flex', alignItems: 'center',
          justifyContent: { xs: 'center', md: flip ? 'flex-end' : 'flex-start' }, gap: 0.75,
        }}>
          {band.artistName}
          <ArrowForwardIosIcon sx={{ fontSize: 9 }} />
        </Typography>
      </Box>
    </Box>
  );
};

const EndOfListBlock: React.FC<{ total: number; onShuffle: () => void }> = ({ total, onShuffle }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h5" fontWeight={700} gutterBottom>You've seen all {total} artists</Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Feeling lucky? Let us pick one for you.</Typography>
    <Button variant="contained" size="large" startIcon={<ShuffleIcon />} onClick={onShuffle}>Random Artist</Button>
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const BandList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeDeactivated = ['admin', 'gm', 'superadmin'].includes(user?.role ?? '');

  const [recommendations, setRecommendations] = useState<BandRecommendation[]>([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState<BandRecommendation[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [strandsLoading, setStrandsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [strands, setStrands] = useState<Strand[]>([]);
  const [presetMenuAnchor, setPresetMenuAnchor] = useState<null | HTMLElement>(null);
  const [featuredBand, setFeaturedBand] = useState<BandRecommendation | null>(null);
  const [quoteSeed] = useState(() => Math.floor(Math.random() * 100));
  const [sortOrder, setSortOrder] = useState<'default' | 'az'>('default');

  // Player state
  const [queue, setQueue] = useState<Set<string>>(new Set());
  const [playNowId, setPlayNowId] = useState<string | null>(null);
  const [activeBandId, setActiveBandId] = useState<string | null>(null);
  const [playerIsPlaying, setPlayerIsPlaying] = useState(false);

  interface Filters {
    genre: string[];
    minMaleMembers: number;
    minFemaleMembers: number;
    dateFrom: Date | null;
    dateTo: Date | null;
    searchTerm: string;
    event: string;
    bandSize: string;
    strand: string;
    instrument: string;
  }

  const [filters, setFilters] = useState<Filters>({
    genre: [], minMaleMembers: 0, minFemaleMembers: 0,
    dateFrom: null, dateTo: null, searchTerm: '', event: '',
    bandSize: '', strand: '', instrument: '',
  });

  // Persist filters
  useEffect(() => {
    const saved = localStorage.getItem('bandListFilters');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFilters(prev => ({ ...prev, ...parsed, dateFrom: parsed.dateFrom ? new Date(parsed.dateFrom) : null, dateTo: parsed.dateTo ? new Date(parsed.dateTo) : null }));
      } catch {}
    }
  }, []);
  useEffect(() => { localStorage.setItem('bandListFilters', JSON.stringify(filters)); }, [filters]);

  const fetchStrands = async () => {
    try { setStrandsLoading(true); const r = await axiosInstance.get<Strand[]>('/api/strands'); setStrands(r.data); }
    catch {} finally { setStrandsLoading(false); }
  };

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const endpoint = canSeeDeactivated ? '/api/admin/bands/all' : '/api/bandrecommendations';
      const response = await axiosInstance.get<BandRecommendation[]>(endpoint);
      const data = Array.isArray(response.data) ? response.data : [];
      setRecommendations(data);
      setFeaturedBand(getDailyFeaturedBand(data.filter(b => !b.isDeactivated)));
      setError(null);
    } catch {
      setError('Failed to fetch recommendations. Please try again later.');
    } finally { setLoading(false); }
  };

  const fetchEvents = async () => {
    try { const r = await axiosInstance.get<Event[]>('/api/events'); setEvents(r.data); } catch {}
  };

  useEffect(() => { fetchRecommendations(); fetchEvents(); fetchStrands(); }, []);

  const debouncedApplyFilters = useCallback(
    debounce(() => {
      const filtered = recommendations.filter((rec) => {
        const totalMembers = rec.maleMembers + rec.femaleMembers;
        const createdDate = new Date(rec.createdAt);
        return (
          (filters.genre.length === 0 || filters.genre.some(genre => rec.genres.includes(genre))) &&
          rec.maleMembers >= filters.minMaleMembers &&
          rec.femaleMembers >= filters.minFemaleMembers &&
          (filters.dateFrom === null || createdDate >= filters.dateFrom) &&
          (filters.dateTo === null || createdDate <= filters.dateTo) &&
          (filters.searchTerm === '' ||
            rec.artistName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            rec.recommendationNotes.toLowerCase().includes(filters.searchTerm.toLowerCase())) &&
          (filters.strand === '' || rec.selectedStrand === filters.strand) &&
          (filters.instrument === '' || (rec.instrumentation || []).some(i =>
            i.instrumentName.toLowerCase().includes(filters.instrument.toLowerCase()))) &&
          (filters.bandSize === '' || (() => {
            const option = bandSizeOptions.find(opt => opt.label === filters.bandSize);
            if (!option) return true;
            return totalMembers >= option.min && totalMembers <= option.max;
          })())
        );
      });
      const sorted = sortOrder === 'az'
        ? [...filtered].sort((a, b) => a.artistName.trim().localeCompare(b.artistName.trim()))
        : filtered;
      setFilteredRecommendations(sorted);
    }, 300),
    [recommendations, filters, sortOrder]
  );

  useEffect(() => { debouncedApplyFilters(); }, [debouncedApplyFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.genre.length > 0) count++;
    if (filters.minMaleMembers > 0) count++;
    if (filters.minFemaleMembers > 0) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.searchTerm) count++;
    if (filters.strand) count++;
    if (filters.bandSize) count++;
    if (filters.instrument) count++;
    return count;
  }, [filters]);

  const getActiveFilterChips = () => {
    const chips = [];
    if (filters.genre.length > 0) chips.push({ label: `Genres: ${filters.genre.join(', ')}`, key: 'genre' });
    if (filters.minMaleMembers > 0) chips.push({ label: `Min male: ${filters.minMaleMembers}`, key: 'minMaleMembers' });
    if (filters.minFemaleMembers > 0) chips.push({ label: `Min female: ${filters.minFemaleMembers}`, key: 'minFemaleMembers' });
    if (filters.dateFrom || filters.dateTo) {
      const dateLabel = filters.dateFrom && filters.dateTo
        ? `${filters.dateFrom.toLocaleDateString()} - ${filters.dateTo.toLocaleDateString()}`
        : filters.dateFrom ? `From: ${filters.dateFrom.toLocaleDateString()}` : `To: ${filters.dateTo?.toLocaleDateString()}`;
      chips.push({ label: `Date: ${dateLabel}`, key: 'date' });
    }
    if (filters.searchTerm) chips.push({ label: `Search: "${filters.searchTerm}"`, key: 'searchTerm' });
    if (filters.strand) {
      const strandName = strands.find(s => s.slug === filters.strand)?.name || filters.strand;
      chips.push({ label: `Strand: ${strandName}`, key: 'strand' });
    }
    if (filters.bandSize) chips.push({ label: `Size: ${filters.bandSize}`, key: 'bandSize' });
    if (filters.instrument) chips.push({ label: `Instrument: ${filters.instrument}`, key: 'instrument' });
    return chips;
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    setFilters(prev => {
      let newValue: any = value;
      if (name === 'minMaleMembers' || name === 'minFemaleMembers') newValue = Number(value);
      return { ...prev, [name]: newValue };
    });
  };

  const handleDateChange = (field: 'dateFrom' | 'dateTo') => (date: Date | null) => {
    setFilters(prev => ({ ...prev, [field]: date }));
  };

  const toggleGenre = (genre: string) => {
    setFilters(prev => ({
      ...prev,
      genre: prev.genre.includes(genre) ? prev.genre.filter(g => g !== genre) : [...prev.genre, genre],
    }));
  };

  const removeFilter = (filterKey: string) => {
    setFilters(prev => {
      const next = { ...prev };
      switch (filterKey) {
        case 'genre': next.genre = []; break;
        case 'minMaleMembers': next.minMaleMembers = 0; break;
        case 'minFemaleMembers': next.minFemaleMembers = 0; break;
        case 'date': next.dateFrom = null; next.dateTo = null; break;
        case 'searchTerm': next.searchTerm = ''; break;
        case 'strand': next.strand = ''; break;
        case 'bandSize': next.bandSize = ''; break;
        case 'instrument': next.instrument = ''; break;
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    setFilters({ genre: [], minMaleMembers: 0, minFemaleMembers: 0, dateFrom: null, dateTo: null, searchTerm: '', event: '', bandSize: '', strand: '', instrument: '' });
  };

  const resetToDefaults = () => { clearAllFilters(); localStorage.removeItem('bandListFilters'); };

  const applyPreset = (preset: typeof FILTER_PRESETS[0]) => {
    setFilters(prev => ({ ...prev, ...preset.filters }));
    setPresetMenuAnchor(null);
  };

  const getRandomBand = () => {
    if (filteredRecommendations.length === 0) return;
    navigate(`/band/${filteredRecommendations[Math.floor(Math.random() * filteredRecommendations.length)]._id}`);
  };

  const exportResults = () => {
    const csvContent = [
      ['Artist Name', 'Country', 'Genres', 'Male Members', 'Female Members', 'Band Size', 'Strand', 'Notes'].join(','),
      ...filteredRecommendations.map(rec => {
        const totalMembers = rec.maleMembers + rec.femaleMembers;
        const bandSizeLabel = bandSizeOptions.find(o => totalMembers >= o.min && totalMembers <= o.max)?.label || `${totalMembers} members`;
        const strandName = rec.selectedStrand ? strands.find(s => s.slug === rec.selectedStrand)?.name || rec.selectedStrand : '';
        return [
          `"${rec.artistName}"`, `"${rec.recommenderCountry}"`, `"${rec.genres.join('; ')}"`,
          rec.maleMembers, rec.femaleMembers, `"${bandSizeLabel}"`, `"${strandName}"`,
          `"${(rec.recommendationNotes || rec.about || '').replace(/"/g, '""').substring(0, 100)}"`
        ].join(',');
      })
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `band-recommendations-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Player helpers
  const playNow = (id: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setPlayNowId(id + '_' + Date.now());
  };

  const toggleQueue = (id: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setQueue(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const handlePlayerStateChange = (bandId: string | null, isPlaying: boolean) => {
    setActiveBandId(bandId);
    setPlayerIsPlaying(isPlaying);
  };

  const playerBands = useMemo(() => {
    if (queue.size === 0) return filteredRecommendations;
    return filteredRecommendations.filter(b => b._id && queue.has(b._id));
  }, [filteredRecommendations, queue]);

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h2" component="h1" gutterBottom>Irish Artists</Typography>
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={180} />
                <CardContent>
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) return (
    <Container maxWidth="lg">
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
        <Button onClick={fetchRecommendations} sx={{ ml: 2 }}>Retry</Button>
      </Alert>
    </Container>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="lg">
      <Typography variant="h2" component="h1" gutterBottom>Irish Artists</Typography>

      {/* Results Counter and Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {`Showing ${filteredRecommendations.length} of ${recommendations.length} bands`}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant={sortOrder === 'default' ? 'contained' : 'outlined'} size="small"
            startIcon={<ShuffleIcon />} onClick={() => setSortOrder('default')}>
            Discover
          </Button>
          <Button variant={sortOrder === 'az' ? 'contained' : 'outlined'} size="small"
            onClick={() => setSortOrder('az')}>
            A – Z
          </Button>
          <Button variant="outlined" startIcon={<ShuffleIcon />} onClick={getRandomBand}
            disabled={filteredRecommendations.length === 0}>
            Random Band
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportResults}
            disabled={filteredRecommendations.length === 0}>
            Export ({filteredRecommendations.length})
          </Button>
          <Button variant="outlined" startIcon={<FilterListIcon />} onClick={(e) => setPresetMenuAnchor(e.currentTarget)}>
            Presets
          </Button>
        </Stack>
      </Box>

      <Menu anchorEl={presetMenuAnchor} open={Boolean(presetMenuAnchor)} onClose={() => setPresetMenuAnchor(null)}>
        {FILTER_PRESETS.map((preset) => (
          <MenuItem key={preset.name} onClick={() => applyPreset(preset)}>{preset.name}</MenuItem>
        ))}
      </Menu>

      {/* Artist of the Day */}
      {featuredBand && <FeaturedHero band={featuredBand} onGenreClick={toggleGenre} />}

      {/* Genre Strip */}
      <GenreStrip bands={recommendations} activeGenres={filters.genre} onToggle={toggleGenre} />

      {/* Recently Added */}
      <RecentlyAddedRow bands={recommendations} />

      {/* Queue indicator */}
      {queue.size > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1, mb: 1, borderRadius: 2,
          bgcolor: 'rgba(80,227,194,0.1)', border: '1px solid rgba(80,227,194,0.3)',
        }}>
          <Typography variant="body2" sx={{ color: '#50E3C2', fontWeight: 600 }}>
            🎵 Playing {queue.size} selected artist{queue.size !== 1 ? 's' : ''}
          </Typography>
          <Button size="small" onClick={() => setQueue(new Set())} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Clear queue
          </Button>
        </Box>
      )}

      {/* Audio Player */}
      {filteredRecommendations.length > 0 && (
        <Box mb={3}>
          <PublicAudioPlayer
            bands={playerBands.length ? playerBands : filteredRecommendations}
            playNowId={playNowId}
            onStateChange={handlePlayerStateChange}
          />
        </Box>
      )}

      {/* Standalone search */}
      <TextField fullWidth name="searchTerm"
        placeholder="Search bands, notes…"
        value={filters.searchTerm}
        onChange={handleFilterChange}
        sx={{ mb: 2 }}
      />

      {/* Filters Accordion */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', gap: 1.5, pr: 1 } }}
        >
          <Badge badgeContent={activeFilterCount} color="primary">
            <Typography fontWeight={600}>Filter Artists</Typography>
          </Badge>
          {activeFilterCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} onClick={e => e.stopPropagation()}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                {[...filters.genre, filters.bandSize, filters.instrument, filters.strand].filter(Boolean).join(' · ')}
              </Typography>
              <Button size="small" variant="outlined"
                onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}
                sx={{ px: 1.5, py: 0.25, minWidth: 0, fontSize: '0.75rem' }}>
                Clear
              </Button>
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>

            {/* Row 1: Genre / Band Size / Strand */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Genre</InputLabel>
                <Select name="genre" multiple value={filters.genre} onChange={handleFilterChange}
                  label="Genre" renderValue={(selected) => (selected as string[]).join(', ')}>
                  <ListSubheader>Popular</ListSubheader>
                  {popularJazzSubgenres.map((genre) => (
                    <MenuItem key={genre} value={genre}
                      disabled={filters.genre.length >= 3 && filters.genre.indexOf(genre) === -1}>
                      <Checkbox checked={filters.genre.indexOf(genre) > -1} />
                      <ListItemText primary={genre} />
                    </MenuItem>
                  ))}
                  <ListSubheader>All Genres</ListSubheader>
                  {jazzSubgenres.filter(g => !popularJazzSubgenres.includes(g)).map((genre) => (
                    <MenuItem key={genre} value={genre}
                      disabled={filters.genre.length >= 3 && filters.genre.indexOf(genre) === -1}>
                      <Checkbox checked={filters.genre.indexOf(genre) > -1} />
                      <ListItemText primary={genre} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Band Size</InputLabel>
                <Select name="bandSize" value={filters.bandSize} onChange={handleFilterChange} label="Band Size">
                  <MenuItem value="">All Sizes</MenuItem>
                  {bandSizeOptions.map((option) => (
                    <MenuItem key={option.label} value={option.label}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Strand</InputLabel>
                <Select name="strand" value={filters.strand} onChange={handleFilterChange}
                  label="Strand" disabled={strandsLoading}>
                  <MenuItem value="">All Strands</MenuItem>
                  {strands.map((strand) => (
                    <MenuItem key={strand.slug} value={strand.slug}>{strand.name}</MenuItem>
                  ))}
                </Select>
                {strandsLoading && <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: '50%', mt: -1 }} />}
              </FormControl>
            </Grid>

            {/* Row 2: Members / Instrument */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth type="number" name="minMaleMembers" label="Min Male Members"
                value={filters.minMaleMembers} onChange={handleFilterChange} inputProps={{ min: 0 }} />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth type="number" name="minFemaleMembers" label="Min Female Members"
                value={filters.minFemaleMembers} onChange={handleFilterChange} inputProps={{ min: 0 }} />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth name="instrument" label="Instrument (e.g. piano, tenor sax, flute)"
                value={filters.instrument} onChange={handleFilterChange}
                helperText="Searches instruments listed in band entries" />
            </Grid>

            {/* Row 3: Date Range / Clear */}
            <Grid item xs={12} sm={6} md={5}>
              <Stack direction="row" spacing={1}>
                <DatePicker label="Added From" value={filters.dateFrom} onChange={handleDateChange('dateFrom')}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                <DatePicker label="Added To" value={filters.dateTo} onChange={handleDateChange('dateTo')}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={clearAllFilters} fullWidth size="small">Clear All</Button>
                <Button variant="text" onClick={resetToDefaults} fullWidth size="small">Reset</Button>
              </Stack>
            </Grid>

          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Active Filter Chips */}
      {activeFilterCount > 0 && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {getActiveFilterChips().map((chip) => (
              <Chip key={chip.key} label={chip.label} onDelete={() => removeFilter(chip.key)}
                size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}

      {/* Band Cards Grid */}
      <Box mt={3}>
        <Grid container spacing={3}>
          {filteredRecommendations.length > 0 ? (<>{(() => {
            const quotableBands = filteredRecommendations.filter(b =>
              b.externalValidation?.some(v => v.quote && v.source)
            );

            return filteredRecommendations.map((rec, index) => {
              const totalMembers = rec.maleMembers + rec.femaleMembers;
              const bandSizeLabel = bandSizeOptions.find(o => totalMembers >= o.min && totalMembers <= o.max)?.label || `${totalMembers} members`;
              const strandName = rec.selectedStrand ? strands.find(s => s.slug === rec.selectedStrand)?.name : '';
              const hasVideo = (rec.youtubeEmbeds?.length ?? 0) > 0;
              const isThisPlaying = activeBandId === rec._id && playerIsPlaying;
              const pressSources = (rec.externalValidation || []).filter(v => v.source).slice(0, 2);

              const card = (
                <Grid item xs={12} sm={6} md={4} key={rec._id}>
                  <Fade in timeout={300} style={{ transitionDelay: `${Math.min(index, 8) * 40}ms` }}>
                    <Link to={`/band/${rec._id}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
                      <Card sx={{
                        height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-6px) scale(1.01)',
                          boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                          '& .card-media': { transform: 'scale(1.08)' },
                          '& .card-overlay': { opacity: 1 },
                        },
                      }}>
                        <Box sx={{ position: 'relative', overflow: 'hidden', height: 180, flexShrink: 0 }}>
                          <CardMedia component="img" className="card-media"
                            sx={{ height: '100%', width: '100%', objectFit: 'cover', transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                            image={rec.mainImage || '/placeholder-image.jpg'}
                            alt={rec.artistName}
                          />

                          {/* Play overlay */}
                          <Box className="card-overlay" sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.6))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: isThisPlaying ? 1 : 0, transition: 'opacity 0.3s ease',
                          }}>
                            {hasVideo ? (
                              <Tooltip title={isThisPlaying ? 'Pause' : 'Play now'}>
                                <Box onClick={(e) => playNow(rec._id!, e)} sx={{
                                  width: 52, height: 52, borderRadius: '50%', bgcolor: '#50E3C2',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer', transition: 'transform 0.2s ease',
                                  '&:hover': { transform: 'scale(1.1)' },
                                }}>
                                  {isThisPlaying
                                    ? <PauseIcon sx={{ color: '#0a0a0f', fontSize: 28 }} />
                                    : <PlayArrowIcon sx={{ color: '#0a0a0f', fontSize: 28 }} />
                                  }
                                </Box>
                              </Tooltip>
                            ) : (
                              <Tooltip title="No video available">
                                <Box sx={{ px: 1.5, py: 0.75, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
                                    No video
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Box>

                          {/* Queue toggle */}
                          {hasVideo ? (
                            <Tooltip title={queue.has(rec._id!) ? 'Remove from queue' : 'Add to queue'}>
                              <Box onClick={(e) => toggleQueue(rec._id!, e)} sx={{
                                position: 'absolute', top: 8, right: 8,
                                width: 30, height: 30, borderRadius: '50%',
                                bgcolor: queue.has(rec._id!) ? '#50E3C2' : 'rgba(0,0,0,0.55)',
                                border: '1.5px solid',
                                borderColor: queue.has(rec._id!) ? '#50E3C2' : 'rgba(255,255,255,0.4)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', backdropFilter: 'blur(4px)',
                                transition: 'all 0.2s ease', zIndex: 2,
                                '&:hover': { transform: 'scale(1.15)', bgcolor: queue.has(rec._id!) ? '#7EECD4' : 'rgba(0,0,0,0.75)' },
                              }}>
                                <Typography sx={{ fontSize: 16, lineHeight: 1, fontWeight: 700, color: queue.has(rec._id!) ? '#0a0a0f' : 'white' }}>
                                  {queue.has(rec._id!) ? '✓' : '+'}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{
                              position: 'absolute', top: 8, right: 8, width: 30, height: 30, borderRadius: '50%',
                              bgcolor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
                            }}>
                              <Typography sx={{ fontSize: 14, lineHeight: 1 }}>🎵</Typography>
                            </Box>
                          )}
                          {/* Deactivated badge */}
                          {rec.isDeactivated && (
                            <Box sx={{
                              position: 'absolute', top: 8, left: 8,
                              px: 1, py: 0.25, borderRadius: 1,
                              bgcolor: 'rgba(237,108,2,0.85)', backdropFilter: 'blur(4px)', zIndex: 2,
                            }}>
                              <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                DEACTIVATED
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h5" component="div" gutterBottom color="text.primary"
                            sx={{
                              transition: 'all 0.3s ease', transformOrigin: 'left center',
                              '.MuiCard-root:hover &': { transform: 'scale(1.05)', color: '#f5f06b' },
                            }}>
                            {rec.artistName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">{rec.recommenderCountry}</Typography>

                          {/* Clickable genre chips */}
                          <Box mt={1} mb={1}>
                            {rec.genres.map((genre) => (
                              <Tooltip key={genre} title={`Filter by ${genre}`} placement="top">
                                <Chip label={genre} size="small"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGenre(genre); }}
                                  sx={{
                                    mr: 0.5, mb: 0.5, cursor: 'pointer',
                                    bgcolor: filters.genre.includes(genre) ? 'primary.main' : undefined,
                                    color: filters.genre.includes(genre) ? 'white' : undefined,
                                    '&:hover': { bgcolor: 'primary.light', color: 'white' },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>

                          <Typography variant="body2" mt={0.5}>
                            {bandSizeLabel}
                            {strandName && ` · ${strandName}`}
                          </Typography>

                          <Typography variant="body2" mt={1} color="text.secondary" sx={{ flex: 1 }}>
                            {stripHtml(rec.recommendationNotes || rec.about || 'No description available.').substring(0, 100)}
                            {stripHtml(rec.recommendationNotes || rec.about || '').length > 100 && '...'}
                          </Typography>

                          {/* Press sources */}
                          {pressSources.length > 0 && (
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {pressSources.map(v => (
                                <Chip key={v.source} label={v.source} size="small" variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20, borderColor: 'primary.light', color: 'primary.main' }} />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  </Fade>
                </Grid>
              );

              // Editorial pull quote after every 9th card
              const shouldInjectQuote = (index + 1) % 9 === 0 && quotableBands.length > 0;
              if (shouldInjectQuote) {
                const quoteSlot = Math.floor(index / 9);
                const quoteBand = quotableBands[(quoteSlot + quoteSeed) % quotableBands.length];
                const quoteEntry = quoteBand.externalValidation?.find(v => v.quote && v.source);
                return (
                  <React.Fragment key={`group-${index}`}>
                    {card}
                    {quoteEntry && (
                      <Grid item xs={12}>
                        <EditorialPullQuote
                          band={quoteBand} quote={quoteEntry.quote!}
                          source={quoteEntry.source} author={quoteEntry.author}
                          flip={quoteSlot % 2 !== 0}
                        />
                      </Grid>
                    )}
                  </React.Fragment>
                );
              }

              return card;
            });
          })()}</>) : (
            <Grid item xs={12}>
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary">
                  No bands found matching your filters
                </Typography>
                <Button onClick={clearAllFilters} sx={{ mt: 2 }}>Clear All Filters</Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {filteredRecommendations.length > 0 && (
        <EndOfListBlock total={filteredRecommendations.length} onShuffle={getRandomBand} />
      )}

      <ScrollToTopButton backgroundColor="#1976d2" color="#ffffff" scrollThreshold={500} size={50} />
    </Container>
  );
};

export default BandList;
